'use strict';
import ClassLogger from 'ClassLogger';

/**
 * @typedef {import('../main').Main} Main
 * @typedef {import('./NavigationHandler').default} NavigationHandler
 */
export default class Accordion {
    /**
     * Returns the class name used by the ClassLogger.
     *
     * @returns {string}
     */
    getClassName () {
        return 'Accordion';
    }

    /**
     * Using this article as inspiration
     * https://css-tricks.com/how-to-animate-the-details-element-using-waapi/
     * @param {NavigationHandler} navigationhandler
     */
    constructor (navigationHandler, options) {
        this.logger = ClassLogger(this, true);
        this.navigationHandler = navigationHandler;

        // Configuration
        this.options = {
            selector: '.c-accordion',
            animationDuration: 400,
            ...options,
        };

        this.navigationHandler
            .on('ready', () => this.init())
            .on('render', () => this.init());
    }

    /**
     * Initialize all accordions on the page
     */
    init () {
        const accordions = [];
        const accordionElements = document.querySelectorAll(this.options.selector);
        accordionElements.forEach((element, index) => {
            const accordionData = this.createAccordionData(element);

            if (accordionData) {
                accordions.push(accordionData);
            }
        });
    }

    /**
     * Create accordion data object for a single element
     * @param {HTMLElement} element - The accordion element
     * @returns {Object|null} Accordion data object or null if invalid
     */
    createAccordionData (element) {
        const summary = element.querySelector('summary.c-accordion__summary');
        const content = element.querySelector('.c-accordion__content');

        // Validate required elements
        if (!summary) {
            this.logger.warn('Warning: summary element not found');
            return null;
        }

        if (!content) {
            this.logger.warn('Warning: content element not found');
            return null;
        }

        // Create accordion data object
        const accordionData = {
            element,
            summary,
            content,
            animation: null,
            isClosing: false,
            isExpanding: false,
        };

        // Bind click handler and store reference
        accordionData.handleClick = (e) => this.handleClick(e, accordionData);
        summary.addEventListener('click', accordionData.handleClick);

        return accordionData;
    }

    /**
     * Handle click events on the summary element
     * @param {Event} e - Click event
     * @param {Object} accordionData - Accordion data object
     */
    handleClick (e, accordionData) {
        // Allow clicks on links or buttons (except toggle button) to propagate
        if (e.target.closest('a') || e.target.closest('button:not(.c-accordion__toggle)')) {
            return true;
        }

        e.preventDefault();
        accordionData.element.style.overflow = 'hidden';

        if (accordionData.isClosing || !accordionData.element.open) {
            this.open(accordionData);
        } else if (accordionData.isExpanding || accordionData.element.open) {
            this.shrink(accordionData);
        }
    }

    /**
     * Closing animation
     * @param {Object} accordionData - Accordion data object
     */
    shrink (accordionData) {
        this.logger.log('Starting shrink animation');

        // Set the element as "being closed"
        accordionData.isClosing = true;
        accordionData.element.classList.add('is-closing');

        // Store the current height of the element
        const startHeight = `${accordionData.element.offsetHeight}px`;
        // Calculate the height of the summary
        const endHeight = `${accordionData.element.offsetHeight - accordionData.content.offsetHeight}px`;

        // If there is already an animation running
        if (accordionData.animation) {
            // Cancel the current animation
            accordionData.animation.cancel();
        }

        // Start a WAAPI animation
        accordionData.animation = accordionData.element.animate({
            // Set the keyframes from the startHeight to endHeight
            height: [startHeight, endHeight],
        }, {
            duration: this.options.animationDuration,
            easing: 'ease-in-out',
        });

        // When the animation is complete, call onAnimationFinish()
        accordionData.animation.onfinish = () => this.onAnimationFinish(accordionData, false);
        // If the animation is cancelled, isClosing variable is set to false
        accordionData.animation.oncancel = () => {
            accordionData.isClosing = false;
            this.logger.log('Shrink animation cancelled');
        };
    }

    /**
     * Trigger accordion open
     * @param {Object} accordionData - Accordion data object
     */
    open (accordionData) {
        this.logger.log('Opening accordion');

        // Apply a fixed height on the element
        accordionData.element.style.height = `${accordionData.element.offsetHeight}px`;
        // Force the [open] attribute on the details element
        accordionData.element.open = true;
        // Wait for the next frame to call the expand function
        window.requestAnimationFrame(() => this.expand(accordionData));
    }

    /**
     * Animate the opening of the accordion element
     * @param {Object} accordionData - Accordion data object
     */
    expand (accordionData) {
        this.logger.log('Starting expand animation');

        accordionData.element.classList.add('is-expanding');
        // Set the element as "being expanding"
        accordionData.isExpanding = true;
        // Get the current fixed height of the element
        const startHeight = `${accordionData.element.offsetHeight}px`;
        // Calculate the open height of the element (summary height + content height)
        const endHeight = `${accordionData.element.offsetHeight + accordionData.content.offsetHeight}px`;

        // If there is already an animation running
        if (accordionData.animation) {
            // Cancel the current animation
            accordionData.animation.cancel();
        }

        // Start a WAAPI animation
        accordionData.animation = accordionData.element.animate({
            // Set the keyframes from the startHeight to endHeight
            height: [startHeight, endHeight],
        }, {
            duration: this.options.animationDuration,
            easing: 'ease-in-out',
        });

        // When the animation is complete, call onAnimationFinish()
        accordionData.animation.onfinish = () => this.onAnimationFinish(accordionData, true);
        // If the animation is cancelled, isExpanding variable is set to false
        accordionData.animation.oncancel = () => {
            accordionData.isExpanding = false;
            this.logger.log('Expand animation cancelled');
        };
    }

    /**
     * Reset our animation when the animation is complete
     * @param {Object} accordionData - Accordion data object
     * @param {boolean} open - Whether the accordion should be open
     */
    onAnimationFinish (accordionData, open) {
        this.logger.log(`Animation finished, open: ${open}`);

        accordionData.element.classList.remove('is-closing');
        accordionData.element.classList.remove('is-expanding');
        // Set the open attribute based on the parameter
        accordionData.element.open = open;
        // Clear the stored animation
        accordionData.animation = null;
        // Reset isClosing & isExpanding
        accordionData.isClosing = false;
        accordionData.isExpanding = false;
        // Remove the overflow hidden and the fixed height
        accordionData.element.style.height = accordionData.element.style.overflow = '';
    }
}
